/**
 * Where "Sign in with SSO" through FOCTTA Core returns (release 3.1):
 *
 *   https://<console>/auth/core-sso/callback?code=...&state=...&iss=...
 *
 * The page checks the state against the one this tab kept when it started the
 * sign-in (sessionStorage, lib/api.ts startCoreSso), wipes the code from the
 * address bar, and sends the callback's query, the PKCE verifier and the nonce
 * to /v1/auth/core-sso/complete. That answers as the password sign-in does: a
 * session, or the MFA step, or a refusal. A Core user without a Discovery
 * account reads who gives access; nothing is created for them.
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2, ShieldAlert } from 'lucide-react';

import { Mark } from '@/components/brand/Mark';
import { CoreSsoError, completeCoreSso, setAuth, takeCoreSsoPending } from '@/lib/api';
import { mfaHandoffTarget, mfaNavigation } from './mfa-handoff';

type Status =
  | { state: 'working' }
  | { state: 'error'; title: string; detail: string; code: string | null };

const CORE_ERRORS: Record<string, string> = {
  access_denied: 'The sign-in at FOCTTA Core was cancelled.',
  login_required: 'FOCTTA Core could not confirm who is signed in. Start again.',
};

export function CoreSsoCallback() {
  const [status, setStatus] = useState<Status>({ state: 'working' });
  // React's development double effect must not spend the code twice.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const query = window.location.search.replace(/^\?/, '');
    const params = new URLSearchParams(query);
    // The code is single use and bound to this tab's verifier, but it does not
    // belong in the history either.
    window.history.replaceState(null, '', window.location.pathname);

    const pending = takeCoreSsoPending();
    const fail = (title: string, detail: string, code: string | null = null): void =>
      setStatus({ state: 'error', title, detail, code });

    const coreError = params.get('error');
    if (coreError) {
      fail(
        'Sign-in did not finish',
        CORE_ERRORS[coreError] ?? params.get('error_description') ?? 'FOCTTA Core ended the sign-in.',
        coreError,
      );
      return;
    }
    if (!pending) {
      fail(
        'This sign-in has expired',
        'Start again from the sign-in page, in the same browser tab.',
      );
      return;
    }
    if (params.get('state') !== pending.state || !params.get('code')) {
      fail(
        'This sign-in does not match',
        'The answer from FOCTTA Core is not for the sign-in this tab started. Start again from the sign-in page.',
      );
      return;
    }

    void completeCoreSso({
      tenantSlug: pending.tenantSlug,
      callbackQuery: query,
      state: pending.state,
      codeVerifier: pending.codeVerifier,
      nonce: pending.nonce,
      redirectUri: pending.redirectUri,
    })
      .then((result) => {
        if ('mfaChallengeRequired' in result && result.mfaChallengeRequired) {
          mfaNavigation.replace(mfaHandoffTarget(result.mfaReason, result.partialAuth.token, ''));
          return;
        }
        if ('session' in result) {
          setAuth(result.session.token, result.user, result.session.expiresAt);
          window.location.replace(pending.returnUrl || '/');
        }
      })
      .catch((err: unknown) => {
        if (err instanceof CoreSsoError && err.code === 'no_discovery_access') {
          fail('No access to FOCTTA Discovery', err.message, err.code);
          return;
        }
        if (err instanceof CoreSsoError && err.code === 'account_suspended') {
          fail('Account suspended', err.message, err.code);
          return;
        }
        fail(
          'Sign-in did not finish',
          err instanceof Error ? err.message : 'The sign-in through FOCTTA Core did not finish.',
          err instanceof CoreSsoError ? err.code : null,
        );
      });
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-6">
          <Mark className="h-12 w-12" />
          {status.state === 'working' ? (
            <div className="flex flex-col items-center gap-3 text-center" role="status">
              <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
              <div>
                <div className="text-lg font-semibold">Signing you in to FOCTTA Discovery…</div>
                <div className="text-sm text-muted-foreground">
                  Completing single sign-on through FOCTTA Core.
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 text-center" data-testid="core-sso-refusal">
              <ShieldAlert className="h-10 w-10 text-amber-600 dark:text-amber-400" aria-hidden />
              <div>
                <div className="text-lg font-semibold">{status.title}</div>
                <div className="mt-2 text-sm text-muted-foreground">{status.detail}</div>
              </div>
              <a
                href="/login"
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Back to sign in
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CoreSsoCallback;
